import React, { useState, useCallback } from 'react'
import { useDisplayTheme } from 'hooks/useUiState'
import { Theme } from 'features/ui/types'
import { useTranslation } from 'react-i18next'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
// @ts-ignore
import { Image, Lists, IconButton, Tooltip } from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { MetricSeries as MetricSeriesType } from 'schemas/MetricSeriesSchema'
import MetricSeries from 'enums/MetricSeries'
import { openLightbox } from 'features/ui/uiSlice'
import { useAppDispatch } from 'app/hooks'
import { RichMentionedUrl } from 'schemas/RichMentionedUrlSchema'
import DefaultMediaDisplayOption from 'enums/DefaultMediaDisplayOption'
import { AutomationId } from 'utils/automationUtil'
import { PopularLinksWidgetsType, PostMetricWithSoundbites } from '../../types'
import DocumentRetrivalRunSearchEffect from '../Soundbite/RunSearchEffect'
import {
  StyledSingleContainer,
  StyledSingleTop,
  StyledSingleTopLink,
  StyledSingleBottom,
  StyledSingleBottomItem,
  StyledSingleBottomItemHightlight,
  StyledSingleBottomVerticalDivider,
  StyledSingleBottomActionItems,
  StyledHideMentionsButtonContainer,
  StyledHideMentionsButton,
  StyledOtherInfoContainer
} from './index.css'
import { useTopLinkHasTooltip, useBottomItemTooltip, useSoundbites, useStreamData } from './hooks'
import MediaGrid, { MediaClickHandler } from './MediaGrid'
import LinkItemSoundbite from './LinkItemSoundbite'
import LoadingSoundbiteGroup from './LoadingSoundbiteGroup'
import { isTwitterPost, filterVideoOrImageMedia } from './utils'
import TwitterActions from './TwitterActions'

type LinkItemProps = {
  postMetric: PostMetricWithSoundbites
  widgetId: number
  onRowClick?: (targetRow: PostMetricWithSoundbites) => void
  index: number
}

const getValueFromSeriesName = (dataSet: MetricSeriesType[], seriesName: string) =>
  dataSet?.find((series: MetricSeriesType) => series?.seriesName === seriesName)?.set?.[0] || 0

const getNumberFormatter = () =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  })

const LinkItem = ({ postMetric, widgetId, onRowClick, index }: LinkItemProps) => {
  const theme = useDisplayTheme() as Theme
  const { defaultMediaDisplayOption } = useDashboardWidgetSetting<PopularLinksWidgetsType>(widgetId)
  const { url, mediaUrl } = postMetric?.mentionedUrls?.[0] as RichMentionedUrl
  const [displayMedia, setDisplayMedia] = useState(
    mediaUrl && defaultMediaDisplayOption === DefaultMediaDisplayOption.EXPAND_ALL_MEDIA
  )
  const [displayMentions, setDisplayMentions] = useState(false)
  const [displayActionButtons, setDisplayActionButtons] = useState(false)
  const { displayTopLinkTooltip, topLinkContentRef } = useTopLinkHasTooltip()
  const { displayBottomItemTooltip, bottomItemContentRef } = useBottomItemTooltip()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const documentRetrievalResult = useSoundbites(widgetId, url!)
  const streamData = useStreamData(postMetric)

  const mediaClickHandler: MediaClickHandler = useCallback(
    (mediaUrls, idx) => {
      dispatch(openLightbox({ slides: mediaUrls, defaultIndex: idx }))
    },
    [dispatch]
  )

  return (
    <StyledSingleContainer
      data-testid={`${AutomationId.popularlinks.linkItem}-${index}`}
      mode={theme}
      onMouseOver={() => setDisplayActionButtons(true)}
      onMouseOut={() => setDisplayActionButtons(false)}
      onClick={onRowClick ? () => onRowClick(postMetric) : undefined}>
      <StyledSingleTop mode={theme} ref={topLinkContentRef}>
        <Tooltip
          mode={theme}
          content={<span>{postMetric?.mentionedUrls?.[0]?.url}</span>}
          enabled={displayTopLinkTooltip}>
          <StyledSingleTopLink
            mode={theme}
            href={postMetric.url}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => {
              event.stopPropagation()
            }}>
            {postMetric?.mentionedUrls?.[0]?.url}
          </StyledSingleTopLink>
        </Tooltip>
      </StyledSingleTop>
      <StyledSingleBottom>
        <StyledSingleBottomItem mode={theme}>
          <StyledSingleBottomItemHightlight mode={theme}>
            {getNumberFormatter().format(
              getValueFromSeriesName(postMetric.dataset!, MetricSeries.TOP_LINKS)
            )}
          </StyledSingleBottomItemHightlight>
          &nbsp;
          <Tooltip
            mode={theme}
            content={<span>{t('linkMentions')}</span>}
            enabled={displayBottomItemTooltip}>
            <span>{t('linkMentions')}</span>
          </Tooltip>
        </StyledSingleBottomItem>
        <StyledSingleBottomVerticalDivider mode={theme} />
        <StyledSingleBottomItem mode={theme} ref={bottomItemContentRef}>
          <StyledSingleBottomItemHightlight mode={theme}>
            {getNumberFormatter().format(
              getValueFromSeriesName(postMetric.dataset!, MetricSeries.IMPRESSIONS)
            )}
          </StyledSingleBottomItemHightlight>
          &nbsp;
          <Tooltip
            mode={theme}
            content={<span>{t('potentialImpression_other')}</span>}
            enabled={displayBottomItemTooltip}>
            <span>{t('potentialImpression_other')}</span>
          </Tooltip>
        </StyledSingleBottomItem>
        {displayActionButtons && (
          <StyledSingleBottomActionItems mode={theme}>
            {!!filterVideoOrImageMedia(postMetric?.mentionedUrls)?.length && (
              <Tooltip mode={theme} content={displayMedia ? t('hideMedia') : t('showMedia')}>
                <IconButton
                  mode={theme}
                  onClick={(event: React.MouseEvent) => {
                    event.stopPropagation()
                    setDisplayMedia(!displayMedia)
                  }}>
                  <Image fontSize={14} color={sys[theme].interaction.primary.default} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip mode={theme} content={displayMentions ? t('hideMentions') : t('showMentions')}>
              <IconButton
                mode={theme}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation()
                  setDisplayMentions(!displayMentions)
                }}>
                <Lists fontSize={14} color={sys[theme].interaction.primary.default} />
              </IconButton>
            </Tooltip>
            {isTwitterPost(streamData) && <TwitterActions postId={streamData.postId!} />}
          </StyledSingleBottomActionItems>
        )}
      </StyledSingleBottom>
      <StyledOtherInfoContainer
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation()
        }}>
        {displayMedia && <MediaGrid postMetric={postMetric} onMediaClick={mediaClickHandler} />}
        {displayMentions && (
          <DocumentRetrivalRunSearchEffect widgetId={widgetId} url={url!}>
            {documentRetrievalResult?.soundbitesDataStatus === 'succeeded' ? (
              <>
                <LinkItemSoundbite url={url!} widgetId={widgetId} />
                <StyledHideMentionsButtonContainer mode={theme}>
                  <StyledHideMentionsButton
                    data-testid={AutomationId.popularlinks.hideMentionsButton}
                    mode={theme}
                    onClick={() => {
                      setDisplayMentions(!displayMentions)
                    }}>
                    {t('hideMentions')}
                  </StyledHideMentionsButton>
                </StyledHideMentionsButtonContainer>
              </>
            ) : (
              <LoadingSoundbiteGroup />
            )}
          </DocumentRetrivalRunSearchEffect>
        )}
      </StyledOtherInfoContainer>
    </StyledSingleContainer>
  )
}

export default LinkItem
